import {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { useDraggable } from "@dnd-kit/react";
import ThreeDotsVerticalIcon from "../../../../../../../assets/svg/three-dots-vertical-icon";
import VerbachatIcon from "../../../../../../../assets/svg/verbachat-icon";
import FlaggedIcon from "../../../../../../../assets/svg/flagged-icon";
import { Tooltip } from "../../../../../../../components/Tooltip/Tooltip";
import { Avatar } from "../../../../../../../components/Avatar/Avatar";
import { HelpDeskTicket } from "../utils/types";
import {
  getCategoryStyle,
  getPriorityColor,
  getPriorityDisplayLabel,
} from "../utils/functions";
import HomeIcon from "../../../../../../../assets/svg/home-icon";
import HashtagIcon from "../../../../../../../assets/svg/hashtag-icon";
import { getRelativeDateText } from "../../../../../../../utils/functions/get-relative-date-text";
import { getDateTimeByFormat } from "../../../../../../../redux/slices/dateTimeFormatSlice";
import { TicketCardActionMenu } from "./TicketCardActionMenu";
import { ShareTicketModal } from "./ShareTicketModal";
import DeleteTicketModal from "./DeleteTicketModal";
import { customToast } from "../../../../../../../utils/custom-toast/custom-toast";
import {
  useDeleteTicketMutation,
  useUpdateTicketMutation,
  useGetTicketCategoriesQuery,
  useGetHelpdeskResolutionReasonsQuery,
} from "../../../../../../../redux/services/helpdeskApi";
import { useOrgUsersList } from "../../../../../../../hooks/useOrgUsersList";
import { renderSystemIcon } from "../../KnowledgeBase/utils/categoryIcons";
import {
  AgentAssignmentDropdown,
  type AgentUser,
} from "../../../../AgentAssignmentDropdown";
import { isHelpdeskResolutionClient } from "../../../utils/function";
import { getExternalVendorOptions } from "../utils/constant";
import { getClientDomain } from "../../../../../../../utils/functions/client-code";
import { getHelpdeskLabels } from "../../../../../../../utils/constants/mappings";
import AddIcon from "../../../../../../../assets/svg/add-icon";
import ChevronDownIcon from "../../../../../../../assets/svg/chevron-down-icon";
import VerifiedIcon from "../../../../../../../assets/svg/VerifiedIcon";
import { TruncatedTextTooltip } from "../../../../../../../components/TruncatedTextTooltip/TruncatedTextTooltip";
import SlaDeadlineTag from "../../TicketDetails/utils/SlaDeadlineTag";
import HourglassIcon from "../../../../../../../assets/svg/hour-glass-icon";
import { PermissionDisabledWrapper } from "../../PermissionDisabledWrapper";
import { useHasHelpdeskUpdateAccess } from "../../../utils/helpdeskPermissions";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../../redux/store";

const selectClientCode = (state: RootState) => state?.auth?.client?.client_code;

interface TicketCardProps {
  ticket: HelpDeskTicket;
  stageKey: string;
  className?: string;
}

const capitalize = (str: string) =>
  str ? str?.charAt(0)?.toUpperCase() + str?.slice(1)?.toLowerCase() : "";

export const TicketCard = ({
  ticket,
  stageKey,
  className,
}: TicketCardProps) => {
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [visibleTagCount, setVisibleTagCount] = useState(0);

  const canUpdate = useHasHelpdeskUpdateAccess(ticket?.ticket_category);

  const moreButtonRef = useRef<HTMLButtonElement>(null);
  const tagsContainerRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();

  const { ref: draggableRef, isDragging } = useDraggable({
    id: `ticket-${ticket?.ticket_id}`,
    disabled: !canUpdate,
    data: {
      type: "ticket",
      ticketId: ticket?.ticket_id,
      fromStageKey: stageKey,
      ticket,
    },
  });

  const [deleteTicket, { isLoading: isDeleting }] = useDeleteTicketMutation();
  const [updateTicket] = useUpdateTicketMutation();
  const { activeUsers } = useOrgUsersList();
  const { data: ticketCategoriesData } = useGetTicketCategoriesQuery();

  const categoryDisplayName = useMemo(() => {
    const matchedCategory = (ticketCategoriesData ?? [])?.find(
      (cat) => cat?.key === ticket?.ticket_category
    );
    return capitalize(matchedCategory?.display_name ?? "");
  }, [ticketCategoriesData, ticket?.ticket_category]);

  const agentsList = useMemo<AgentUser[]>(
    () =>
      activeUsers.map((user) => ({
        id: user._id,
        name: user.name,
        email: user.email,
        role: user.role,
        status: user.status,
      })),
    [activeUsers]
  );

  const [localAssignees, setLocalAssignees] = useState<string[]>(
    ticket?.assignees ?? []
  );

  useEffect(() => {
    setLocalAssignees(ticket?.assignees ?? []);
  }, [ticket?.assignees]);

  const resolvedAgents = useMemo(
    () =>
      localAssignees
        .map((id) =>
          agentsList.find(
            (a) =>
              a?.id === id || (a as AgentUser & { _id?: string })?._id === id
          )
        )
        .filter(Boolean) as AgentUser[],
    [localAssignees, agentsList]
  );

  const handleAssigneeApply = async (
    agentIds: string[],
    _users: AgentUser[]
  ) => {
    try {
      setLocalAssignees(agentIds);
      await updateTicket({
        ticketId: String(ticket?.ticket_id),
        assignees: agentIds,
      }).unwrap();
    } catch {
      setLocalAssignees(ticket?.assignees ?? []);
      customToast("Failed to update assignees", "error");
    }
  };

  const assigneeTrigger = useMemo(() => {
    if (resolvedAgents.length === 0) {
      return (
        <div className="flex items-center gap-[2px] group/assignee">
          <AddIcon className="text-verba-blue dark:text-verba-blue-dk w-[18px] h-[18px]" />
          <span className="text-md font-medium text-verba-blue dark:text-verba-blue-dk group-hover/assignee:underline">
            Add Assignee
          </span>
        </div>
      );
    }

    if (resolvedAgents.length === 1) {
      return (
        <div className="flex items-center gap-1.5">
          <Avatar
            text={resolvedAgents[0]?.name || ""}
            maxChars={1}
            size={18}
            className="text-[8px]"
          />
          <span className="text-sm font-medium text-verba-indigo dark:text-verba-indigo-dk truncate max-w-[100px]">
            {resolvedAgents[0]?.name || "-"}
          </span>
          <div className="w-px h-[14px] bg-border-gray dark:bg-border-gray-dk" />
          <ChevronDownIcon className="w-[10px] h-[10px] text-verba-indigo dark:text-verba-indigo-dk shrink-0" />
        </div>
      );
    }

    return (
      <div className="flex items-center gap-1.5">
        <div className="flex items-center -space-x-1.5">
          {resolvedAgents.map((agent) => (
            <Avatar
              key={agent?.id}
              text={agent?.name || ""}
              maxChars={1}
              size={18}
              className="text-[8px] ring-1 ring-pure-white dark:ring-pure-white-dk"
            />
          ))}
        </div>
        <div className="w-px h-[14px] bg-border-gray dark:bg-border-gray-dk" />
        <ChevronDownIcon className="w-[10px] h-[10px] text-verba-indigo dark:text-verba-indigo-dk shrink-0" />
      </div>
    );
  }, [resolvedAgents]);

  const isResolutionEnabled = isHelpdeskResolutionClient(getClientDomain());
  const clientCode = useSelector(selectClientCode);
  const region = useSelector((state: RootState) => state?.auth?.client?.region);
  const helpdeskLabels = getHelpdeskLabels(region);

  const { data: resolutionReasonsData } = useGetHelpdeskResolutionReasonsQuery(
    {},
    { skip: !isResolutionEnabled }
  );

  const isResolutionReasonValid = useMemo(() => {
    if (!ticket?.resolution_reason) return false;
    const raw = resolutionReasonsData as
      | { reasons?: string[] }
      | string[]
      | undefined;
    const reasons = Array.isArray(raw) ? raw : (raw?.reasons ?? []);
    return reasons.includes(ticket.resolution_reason);
  }, [resolutionReasonsData, ticket?.resolution_reason]);

  const isExternalVendorValid = useMemo(() => {
    if (!ticket?.external_vendor) return false;
    return getExternalVendorOptions(clientCode).includes(ticket.external_vendor);
  }, [clientCode, ticket?.external_vendor]);

  const priorityLabel = getPriorityDisplayLabel(ticket?.priority);
  const priorityColor = getPriorityColor(priorityLabel);

  const handleCardClick = () => {
    navigate(`/dashboard/crm/helpdesk/all-tickets/${ticket?.ticket_id}`);
  };

  const handleMoreOptionsClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();
    const willOpen = !showActionMenu;
    if (willOpen) {
      window.dispatchEvent(
        new CustomEvent("close-ticket-menus", {
          detail: ticket?.ticket_id,
        })
      );
    }
    setShowActionMenu(willOpen);
  };

  useEffect(() => {
    const handleCloseMenus = (e: Event) => {
      if ((e as CustomEvent)?.detail !== ticket?.ticket_id) {
        setShowActionMenu(false);
      }
    };
    window.addEventListener("close-ticket-menus", handleCloseMenus);
    return () =>
      window.removeEventListener("close-ticket-menus", handleCloseMenus);
  }, [ticket?.ticket_id]);

  const tags = useMemo(() => ticket?.tags ?? [], [ticket?.tags]);

  useLayoutEffect(() => {
    const container = tagsContainerRef?.current;
    if (!container || tags?.length === 0) {
      setVisibleTagCount(0);
      return;
    }

    const calculate = () => {
      const containerWidth = container?.offsetWidth;
      if (containerWidth === 0) return;
      const gap = 6;
      const tagPadX = 12;
      const badgePadX = 12;
      const maxRows = 2;

      const categoryEl = container?.firstElementChild as HTMLElement | null;
      const categoryWidth = categoryEl ? categoryEl?.offsetWidth : 0;

      const span = document?.createElement("span");
      span.className = "text-sm font-normal whitespace-nowrap";
      span.style.cssText =
        "position:absolute;visibility:hidden;pointer-events:none;";
      document?.body?.appendChild(span);

      const tagWidths = tags?.map((t) => {
        span.textContent = t;
        return span?.offsetWidth + tagPadX;
      });

      span.textContent = `+${tags?.length}`;
      const badgeWidth = span?.offsetWidth + badgePadX;
      document?.body?.removeChild(span);

      let row = 1;
      let used = categoryWidth + gap;
      let count = 0;

      for (let i = 0; i < tags?.length; i++) {
        const gapBefore = gap;
        const remaining = tags?.length - (i + 1);
        const badgeReserve = remaining > 0 ? gap + badgeWidth : 0;
        const needed = gapBefore + tagWidths[i] + badgeReserve;

        if (used + needed <= containerWidth) {
          used += gapBefore + tagWidths[i];
          count++;
        } else if (row < maxRows) {
          row++;
          used = tagWidths[i];
          const badgeReserveNew = remaining > 0 ? gap + badgeWidth : 0;
          if (used + badgeReserveNew <= containerWidth) {
            count++;
          } else {
            break;
          }
        } else {
          break;
        }
      }

      setVisibleTagCount(Math.max(1, count));
    };

    calculate();

    const observer = new ResizeObserver(calculate);
    observer?.observe(container);
    return () => observer?.disconnect();
  }, [tags]);

  const handleDeleteTicket = () => {
    customToast(helpdeskLabels.ticketDeletedSuccess, "success");
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteTicket(String(ticket?.ticket_id))?.unwrap();
      handleDeleteTicket();
      setShowDeleteModal(false);
    } catch {
      customToast(helpdeskLabels.failedToDeleteTicket, "error");
    }
  };

  const handleScrollToggle = useCallback((lock: boolean) => {
    const containers = document.querySelectorAll(
      "[data-ticket-stage-crm-scroll-container]"
    );
    containers.forEach((el) => {
      (el as HTMLElement).style.overflowY = lock ? "hidden" : "auto";
      (el as HTMLElement).style.overflowX = lock ? "hidden" : "auto";
    });
  }, []);

  return (
    <div
      ref={draggableRef}
      className={`relative ${canUpdate ? "cursor-grab active:cursor-grabbing" : ""} ${isDragging ? "opacity-60" : ""}`}
    >
      <div
        onClick={handleCardClick}
        className={`group bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg w-[280px] overflow-hidden transition-shadow duration-200 cursor-pointer group-hover:shadow-card ${className}`}
      >
        <div className="flex flex-col gap-[10px] px-3 py-[10px]">
          {ticket?.stage_key !== "closed" && (
            <SlaDeadlineTag dueDate={ticket?.due_date} variant="compact" />
          )}
          <div className="w-full min-h-[26px] flex items-start justify-between gap-2.5">
            <div className="flex flex-col gap-[10px] min-w-0">
              <span className="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk line-clamp-2">
                {ticket?.title}
              </span>
              {isResolutionEnabled && isResolutionReasonValid && (
                <div className="min-h-[22px] flex items-center gap-1 border border-tag-green-stroke dark:border-tag-green-stroke-dk rounded-md px-2 py-[2px] w-max max-w-full">
                  <VerifiedIcon className="w-3.5 h-3.5 text-verba-green-success dark:text-verba-green-success-dk shrink-0" />
                  <TruncatedTextTooltip
                    text={ticket?.resolution_reason ?? ""}
                    maxWidth="max-w-[200px]"
                    className="text-sm font-medium text-verba-green-success dark:text-verba-green-success-dk"
                  />
                </div>
              )}
              {isResolutionEnabled && isExternalVendorValid && (
                <div className="min-h-[22px] flex items-center gap-1 border bg-tags-lavender dark:bg-tags-lavender-dk border-tag-lavender-stroke dark:border-tag-lavender-stroke-dk rounded-md px-2 py-[2px] w-max max-w-full">
                  <HourglassIcon className="w-3.5 h-3.5 text-tag-scheduled dark:text-tag-scheduled-dk shrink-0" />
                  <TruncatedTextTooltip
                    text={ticket?.external_vendor ?? ""}
                    maxWidth="max-w-[200px]"
                    className="text-sm font-medium text-tag-scheduled dark:text-tag-scheduled-dk"
                  />
                </div>
              )}
            </div>

            <div
              className={`${showActionMenu ? "flex" : "group-hover:flex hidden"} items-center shrink-0 w-max`}
            >
              <button
                ref={moreButtonRef}
                data-three-dots-button
                onClick={handleMoreOptionsClick}
                className="group/more flex items-center justify-center p-0.5 rounded hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors"
                aria-label="More options"
              >
                <ThreeDotsVerticalIcon className="w-[18px] h-[18px] text-gray-placeholder dark:text-gray-placeholder-dk group-hover/more:text-verba-indigo dark:group-hover/more:text-verba-indigo-dk transition-colors" />
              </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 items-center w-full">
            {priorityLabel && (
              <>
                <div className="flex gap-[2px] items-center">
                  <FlaggedIcon className={`w-3 h-3 ${priorityColor}`} />
                  <span className={`text-sm font-medium ${priorityColor}`}>
                    {priorityLabel}
                  </span>
                </div>
                <div className="h-[10px] w-px bg-border-gray dark:bg-border-gray-dk shrink-0" />
              </>
            )}

            <div className="flex gap-[2px] items-center">
              <VerbachatIcon className="w-3 h-3 text-verba-blue dark:text-verba-blue-dk" />
              <span className="text-xxs font-normal text-data dark:text-data-dk">
                {ticket?.total_comments_count ?? 0}
              </span>
            </div>

            {!!ticket?.custom_fields?.["system-name"] && (
              <>
                <div className="h-[10px] w-px bg-border-gray dark:bg-border-gray-dk shrink-0" />
                <div className="bg-pure-white dark:bg-pure-white-dk border-[0.909px] border-border-gray dark:border-border-gray-dk flex gap-[3.636px] items-center max-w-[127px] px-[7.273px] py-[1.818px] rounded-[5.455px] shadow-xs-blur">
                  {renderSystemIcon(
                    String(ticket?.custom_fields?.["system-name"])
                  )}
                  <p className="flex-1 text-sm font-medium text-verba-indigo dark:text-verba-indigo-dk leading-[16.364px] overflow-hidden text-ellipsis whitespace-nowrap">
                    {String(ticket?.custom_fields?.["system-name"])}
                  </p>
                </div>
              </>
            )}
          </div>

          <div className="flex items-center gap-[6px] flex-wrap">
            <div className="flex items-center gap-[2px]">
              <HomeIcon className="w-[14px] h-[14px] text-gray-placeholder dark:text-gray-placeholder-dk shrink-0" />
              <span className="text-sm font-normal text-verba-indigo dark:text-verba-indigo-dk">
                {ticket?.property_name}
              </span>
            </div>
            <div className="h-[10px] w-px bg-border-gray dark:bg-border-gray-dk shrink-0" />
            <div className="flex items-center gap-[2px]">
              <HashtagIcon className="w-4 h-4 text-gray-placeholder dark:text-gray-placeholder-dk shrink-0" />
              <span className="text-sm font-normal text-dark-gray dark:text-dark-gray-dk">
                {String(ticket?.ticket_id)}
              </span>
            </div>
          </div>

          <div
            ref={tagsContainerRef}
            className="flex gap-[6px] items-center flex-wrap"
          >
            {categoryDisplayName && (
              <span
                className={`text-sm font-medium px-2 py-0.5 rounded border shrink-0 ${getCategoryStyle(categoryDisplayName)}`}
              >
                {categoryDisplayName}
              </span>
            )}
            {tags?.slice(0, visibleTagCount)?.map((tag, index) => (
              <div
                key={index}
                className="bg-tag-gray dark:bg-tag-gray-dk flex items-center px-[6px] py-[3px] rounded shrink-0"
              >
                <span className="text-sm font-normal text-data dark:text-data-dk whitespace-nowrap">
                  {tag}
                </span>
              </div>
            ))}
            {tags?.length > visibleTagCount && visibleTagCount > 0 && (
              <div className="bg-tag-gray dark:bg-tag-gray-dk flex items-center px-[6px] py-[3px] rounded shrink-0">
                <span className="text-sm font-normal text-data dark:text-data-dk whitespace-nowrap">
                  +{tags?.length - visibleTagCount}
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between w-full">
            <div onClick={(e) => e.stopPropagation()}>
              <PermissionDisabledWrapper disabled={!canUpdate}>
                <AgentAssignmentDropdown
                  selectedAgentIds={localAssignees}
                  onApply={handleAssigneeApply}
                  agentsList={agentsList}
                  subHeading={helpdeskLabels.assignTicketSubheading}
                  maxAgents={4}
                  hideRole
                  canSaveEmpty
                  title="ASSIGNEES"
                  triggerText="Add Assignee"
                  customTrigger={assigneeTrigger}
                  onToggleScroll={handleScrollToggle}
                />
              </PermissionDisabledWrapper>
            </div>
            <Tooltip
              content="Last Activity"
              side="top"
              contentClassName="text-xs"
            >
              <p className="text-sm font-normal text-dark-gray dark:text-dark-gray-dk whitespace-nowrap">
                {getRelativeDateText(ticket?.updated_at)} at{" "}
                {getDateTimeByFormat(new Date(ticket?.updated_at))?.time}
              </p>
            </Tooltip>
          </div>
        </div>
      </div>
      {showActionMenu && (
        <TicketCardActionMenu
          ticket={ticket}
          triggerRef={moreButtonRef}
          onDeleteClick={() => {
            setShowActionMenu(false);
            setShowDeleteModal(true);
          }}
          onClose={() => setShowActionMenu(false)}
          onShare={() => setShowShareModal(true)}
          canUpdate={canUpdate}
        />
      )}
      <ShareTicketModal
        ticketId={ticket?.ticket_id}
        open={showShareModal}
        onOpenChange={setShowShareModal}
      />
      <DeleteTicketModal
        open={showDeleteModal}
        onOpenChange={setShowDeleteModal}
        onConfirmDelete={handleConfirmDelete}
        ticket={ticket}
        isDeleting={isDeleting}
      />
    </div>
  );
};
